import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Target, TrendingUp, Users, Phone, Mail, AlertTriangle, Package, Eye, FileText, HandCoins } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Rotación comercial — para vendedores:
 *  - Resumen (KPIs + top productos del periodo)
 *  - Clientes sin venta en N días (para priorizar contacto)
 *  - Histórico de productos por cliente (para ofrecer repetición)
 */
export default function RotacionComercial() {
  const [tab, setTab] = useState("resumen");
  const [dias, setDias] = useState(30);
  const [resumen, setResumen] = useState(null);
  const [clientesSinVenta, setClientesSinVenta] = useState([]);
  const [misFacturas, setMisFacturas] = useState(null);
  const [misCobros, setMisCobros] = useState(null);
  const [q, setQ] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [productosCliente, setProductosCliente] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadResumen = async (d) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/rotacion/resumen?dias=${d}`);
      setResumen(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const loadSinVenta = async (d) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/rotacion/clientes-sin-venta?dias=${d}`);
      setClientesSinVenta(data || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { loadResumen(dias); loadSinVenta(dias); }, []);

  const aplicarDias = () => { loadResumen(dias); loadSinVenta(dias); };

  const verProductos = async (cli) => {
    setSelectedClient(cli);
    try {
      const { data } = await api.get(`/rotacion/productos-por-cliente/${cli.id}?dias=365`);
      setProductosCliente(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadMisFacturas = async () => {
    if (misFacturas) return; // cache simple
    try {
      setLoading(true);
      const { data } = await api.get(`/invoicing/facturas`);
      setMisFacturas(data || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const loadMisCobros = async () => {
    if (misCobros) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/accounting/cobros`);
      setMisCobros(data || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "facturas") loadMisFacturas();
    if (tab === "cobros") loadMisCobros();
  }, [tab]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    if (!term) return clientesSinVenta;
    return clientesSinVenta.filter(c =>
      (c.razon_social || "").toLowerCase().includes(term) ||
      (c.numero_identificacion || "").toLowerCase().includes(term) ||
      (c.ciudad || "").toLowerCase().includes(term),
    );
  }, [clientesSinVenta, q]);

  return (
    <div className="space-y-6" data-testid="rotacion-comercial-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Ventas</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Target className="inline mr-2 mb-1" size={28} /> Rotación de mis ventas
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">
          Priorice clientes sin venta reciente y revise el histórico de productos vendidos para ofrecer reposición.
        </p>
      </header>

      <div className="bg-white rounded-md border border-[#d1dbd6] p-4 flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Periodo de análisis:</label>
        <Select value={String(dias)} onValueChange={(v) => setDias(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="periodo-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="15">Últimos 15 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="60">Últimos 60 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
            <SelectItem value="180">Últimos 6 meses</SelectItem>
            <SelectItem value="365">Último año</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={aplicarDias} size="sm" className="bg-[#205c45]" disabled={loading} data-testid="apply-periodo">Aplicar</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="resumen" data-testid="tab-resumen"><TrendingUp size={14} className="mr-1.5" />Resumen</TabsTrigger>
          <TabsTrigger value="sin-venta" data-testid="tab-sin-venta"><AlertTriangle size={14} className="mr-1.5" />Clientes sin venta</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico"><Package size={14} className="mr-1.5" />Histórico por cliente</TabsTrigger>
          <TabsTrigger value="facturas" data-testid="tab-mis-facturas"><FileText size={14} className="mr-1.5" />Mis facturas</TabsTrigger>
          <TabsTrigger value="cobros" data-testid="tab-mis-cobros"><HandCoins size={14} className="mr-1.5" />Mi cartera de cobros</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-4">
          {resumen && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Clientes asignados" value={resumen.n_clientes_asignados} />
                <Kpi label="Clientes con venta" value={resumen.n_clientes_con_venta} tone="success" />
                <Kpi label="Clientes sin venta" value={resumen.n_clientes_sin_venta} tone={resumen.n_clientes_sin_venta > 0 ? "warning" : "default"} />
                <Kpi label={`Monto en ${dias}d`} value={`$${(resumen.monto_total_periodo || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              </div>

              <div className="bg-white rounded-md border border-[#d1dbd6] overflow-hidden">
                <div className="px-4 py-3 border-b bg-[#f7f9f8]">
                  <h3 className="font-semibold text-[#15241d]"><Package size={14} className="inline mr-1.5" />Top 10 productos vendidos (periodo)</h3>
                </div>
                {(resumen.top_productos || []).length === 0 ? (
                  <div className="p-10 text-center text-[#8ba397]">Sin ventas en el periodo seleccionado.</div>
                ) : (
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th className="w-10">#</th>
                        <th>Producto</th>
                        <th className="text-right">Veces vendido</th>
                        <th className="text-right">Cantidad</th>
                        <th className="text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody data-testid="top-productos-table">
                      {resumen.top_productos.map((p, i) => (
                        <tr key={p.producto_comercial_id}>
                          <td className="font-bold text-[#205c45]">#{i + 1}</td>
                          <td className="font-medium">{p.nombre}</td>
                          <td className="text-right tabular-nums">{p.veces}</td>
                          <td className="text-right tabular-nums">{p.cantidad}</td>
                          <td className="text-right tabular-nums font-semibold">${p.monto.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="sin-venta" className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Input placeholder="Buscar cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="search-sin-venta" />
            <Badge variant="outline" className="font-mono">{filtered.length} clientes</Badge>
          </div>
          <div className="bg-white rounded-md border border-[#d1dbd6] overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-[#8ba397]">
                {clientesSinVenta.length === 0
                  ? "¡Excelente! Todos tus clientes asignados tuvieron al menos una venta en el periodo."
                  : "Sin resultados."}
              </div>
            ) : (
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>RUC/CI</th>
                    <th>Ciudad</th>
                    <th className="text-right">Días sin venta</th>
                    <th className="text-right">Histórico</th>
                    <th>Contacto</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody data-testid="sin-venta-table">
                  {filtered.map((c) => (
                    <tr key={c.id}>
                      <td className="font-medium">{c.razon_social}</td>
                      <td className="font-mono text-xs">{c.numero_identificacion}</td>
                      <td className="text-xs">{c.ciudad || "—"}</td>
                      <td className="text-right tabular-nums">
                        {c.dias_sin_venta === null ? (
                          <Badge className="bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]">Sin compras aún</Badge>
                        ) : c.dias_sin_venta > 90 ? (
                          <Badge className="bg-[#fef4f2] text-[#b53a25] border border-[#f5c0b7]">{c.dias_sin_venta}d</Badge>
                        ) : (
                          <span className="text-[#5c7368]">{c.dias_sin_venta}d</span>
                        )}
                      </td>
                      <td className="text-right text-xs">
                        {c.n_facturas > 0 ? `${c.n_facturas} fact · $${c.monto_historico.toFixed(2)}` : "—"}
                      </td>
                      <td className="text-xs text-[#4a5c53]">
                        {c.telefono && <a href={`tel:${c.telefono}`} className="flex items-center gap-1 hover:underline"><Phone size={11} />{c.telefono}</a>}
                        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:underline"><Mail size={11} />{c.email}</a>}
                      </td>
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => { setTab("historico"); verProductos(c); }} data-testid={`ver-prods-${c.id}`}>
                          <Eye size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="historico" className="space-y-3">
          <div className="bg-white rounded-md border border-[#d1dbd6] p-4">
            {!selectedClient ? (
              <div className="text-center py-8 text-[#8ba397]">
                <Users className="mx-auto mb-2" size={28} />
                Elija un cliente desde la pestaña &quot;Clientes sin venta&quot; (ícono de ojo) para ver su histórico de productos.
              </div>
            ) : (
              <>
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#5c7368]">Cliente</div>
                    <h3 className="font-bold text-lg text-[#15241d]">{selectedClient.razon_social}</h3>
                    <div className="text-xs text-[#5c7368]">{selectedClient.numero_identificacion} · {selectedClient.ciudad}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedClient(null); setProductosCliente(null); }}>Cambiar cliente</Button>
                </div>
                {productosCliente && productosCliente.productos && productosCliente.productos.length === 0 ? (
                  <div className="text-center py-10 text-[#8ba397]">Sin histórico de ventas en el último año.</div>
                ) : productosCliente ? (
                  <div className="overflow-hidden rounded border border-[#d1dbd6]">
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th>Código</th>
                          <th className="text-right">Veces</th>
                          <th className="text-right">Cantidad</th>
                          <th className="text-right">Último precio</th>
                          <th>Última compra</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody data-testid="productos-cliente-table">
                        {productosCliente.productos.map((p) => (
                          <tr key={p.producto_comercial_id}>
                            <td className="font-medium">{p.producto_nombre}</td>
                            <td className="font-mono text-xs">{p.codigo_interno}</td>
                            <td className="text-right tabular-nums">{p.veces_comprado}</td>
                            <td className="text-right tabular-nums">{p.cantidad_total}</td>
                            <td className="text-right tabular-nums">${p.ultimo_precio.toFixed(4)}</td>
                            <td className="text-xs">{p.ultima_compra?.slice(0, 10)}</td>
                            <td className="text-right tabular-nums font-semibold">${p.monto_total.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-6 text-[#8ba397]">Cargando…</div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* ============ MIS FACTURAS ============ */}
        <TabsContent value="facturas" className="space-y-3">
          <MisFacturas data={misFacturas} loading={loading} />
        </TabsContent>

        {/* ============ MI CARTERA DE COBROS ============ */}
        <TabsContent value="cobros" className="space-y-3">
          <MiCartera data={misCobros} loading={loading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, tone = "default" }) {
  const colorMap = {
    default: "bg-white border-[#d1dbd6] text-[#15241d]",
    success: "bg-[#e9f2ee] border-[#b2d0c2] text-[#205c45]",
    warning: "bg-[#fef7ec] border-[#f5c477] text-[#8a5500]",
  };
  return (
    <div className={`rounded-md border p-4 ${colorMap[tone]}`} data-testid={`kpi-${label}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}


function MisFacturas({ data, loading }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    if (!t) return data;
    return data.filter(f =>
      (f.numero || "").toLowerCase().includes(t) ||
      (f.cliente_nombre || "").toLowerCase().includes(t) ||
      (f.estado_sri || "").toLowerCase().includes(t)
    );
  }, [data, q]);
  const totales = useMemo(() => {
    const r = { total: 0, autorizadas: 0, monto_autorizado: 0 };
    (filtered || []).forEach(f => {
      r.total += Number(f.total || 0);
      if (f.estado_sri === "autorizado") { r.autorizadas++; r.monto_autorizado += Number(f.total || 0); }
    });
    return r;
  }, [filtered]);

  if (loading && !data) return <div className="text-center py-10 text-[#8ba397]">Cargando facturas…</div>;
  if (!data) return null;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Facturas" value={filtered.length} />
        <Kpi label="Autorizadas" value={totales.autorizadas} tone="success" />
        <Kpi label="Monto autorizado" value={`$${totales.monto_autorizado.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} tone="success" />
        <Kpi label="Monto total (todas)" value={`$${totales.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
        <Input placeholder="Buscar por número, cliente o estado…" value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" data-testid="search-mis-facturas" />
        <Badge variant="outline" className="font-mono">{filtered.length} factura(s)</Badge>
      </div>
      <div className="bg-white rounded-md border border-[#d1dbd6] overflow-hidden mt-2">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[#8ba397]">Sin facturas para mostrar.</div>
        ) : (
          <table className="erp-table">
            <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Estado SRI</th><th className="text-right">Total</th><th className="w-16"></th></tr></thead>
            <tbody data-testid="mis-facturas-table">
              {filtered.map(f => (
                <tr key={f.id}>
                  <td className="font-mono text-xs font-semibold">{f.numero}</td>
                  <td className="text-xs">{(f.fecha || f.created_at || "").slice(0, 10)}</td>
                  <td className="text-sm">{f.cliente_nombre}</td>
                  <td>
                    <Badge className={
                      f.estado_sri === "autorizado" ? "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]" :
                      f.estado_sri === "rechazado" ? "bg-[#fef4f2] text-[#b53a25] border border-[#f5c0b7]" :
                      f.estado_sri === "borrador" ? "bg-[#f7f9f8] text-[#5c7368] border border-[#d1dbd6]" :
                      "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]"
                    }>{f.estado_sri || "—"}</Badge>
                  </td>
                  <td className="text-right tabular-nums font-semibold">${(f.total || 0).toFixed(2)}</td>
                  <td>
                    <Link to={`/facturacion/${f.id}`} className="inline-flex items-center text-[#205c45] hover:underline text-xs">
                      <Eye size={14} className="mr-1" />Ver
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function MiCartera({ data, loading }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    if (!t) return data;
    return data.filter(c =>
      (c.numero || "").toLowerCase().includes(t) ||
      (c.cliente_nombre || "").toLowerCase().includes(t) ||
      (c.metodo || "").toLowerCase().includes(t)
    );
  }, [data, q]);
  const total = useMemo(() => (filtered || []).reduce((s, c) => s + Number(c.monto_total || 0), 0), [filtered]);

  if (loading && !data) return <div className="text-center py-10 text-[#8ba397]">Cargando cobros…</div>;
  if (!data) return null;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Kpi label="Cobros registrados" value={filtered.length} />
        <Kpi label="Monto cobrado total" value={`$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} tone="success" />
        <Kpi label="Promedio por cobro" value={filtered.length ? `$${(total / filtered.length).toFixed(2)}` : "$0.00"} />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
        <Input placeholder="Buscar cobro por número, cliente o método…" value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" data-testid="search-mis-cobros" />
        <Badge variant="outline" className="font-mono">{filtered.length} cobro(s)</Badge>
      </div>
      <div className="bg-white rounded-md border border-[#d1dbd6] overflow-hidden mt-2">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[#8ba397]">
            Aún no aparecen cobros en su cartera. Contabilidad debe registrar los pagos contra las facturas de sus clientes.
          </div>
        ) : (
          <table className="erp-table">
            <thead><tr><th>Cobro #</th><th>Fecha</th><th>Cliente</th><th>Método</th><th>Facturas</th><th className="text-right">Monto</th></tr></thead>
            <tbody data-testid="mis-cobros-table">
              {filtered.map(c => (
                <tr key={c.id}>
                  <td className="font-mono text-xs font-semibold">{c.numero}</td>
                  <td className="text-xs">{(c.fecha || "").slice(0, 10)}</td>
                  <td className="text-sm">{c.cliente_nombre || "—"}</td>
                  <td className="text-xs capitalize">{c.metodo || "—"}</td>
                  <td className="text-[11px] font-mono">
                    {(c.aplicaciones || []).map(a => a.factura_numero).filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="text-right tabular-nums font-semibold text-[#205c45]">${(c.monto_total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
